import React, { useState, useEffect } from 'react';
import { Button, Box, DialogContent } from '@mui/material';
import DialogWindow from '../_misc/DialogWindow';
import { useGetUserProfileQuery } from '../../../generated/graphql';
import { useNotification } from '../notification/NotificationContext';
import ProfileUpdateForm from './forms/UserProfileForm';
import PasswordUpdateForm from './forms/UserPasswordForm';
import { LoadingSpinner } from '@/common/components/loading/LoadingSpinner';

interface ProfileDialogProps {
  open?: boolean;
  onClose?: () => void;
}

const ProfileDialog: React.FC<ProfileDialogProps> = ({
  open: propOpen,
  onClose,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const { showError } = useNotification();

  // Use prop value if provided, otherwise use internal state
  const open = propOpen !== undefined ? propOpen : internalOpen;
  const handleClose = onClose || (() => setInternalOpen(false));

  const { loading: profileLoading, error: profileError } =
    useGetUserProfileQuery({
      fetchPolicy: 'network-only',
      skip: !open,
    });

  // Show error notification when profile fetch fails
  useEffect(() => {
    if (profileError) {
      showError('Failed to load profile data');
    }
  }, [profileError, showError]);

  return (
    <>
      {/* Only show button when not controlled by props */}
      {propOpen === undefined && (
        <Button
          variant="outlined"
          onClick={() => setInternalOpen(true)}
          loading={profileLoading}
        >
          Update Profile
        </Button>
      )}

      <DialogWindow
        open={open}
        onClose={handleClose}
        title="Profile"
        maxWidth="lg"
      >
        <DialogContent
          sx={{
            p: 2,
            minHeight: 800,
            minWidth: 400,
          }}
        >
          {profileLoading ? (
            <LoadingSpinner
              size={40}
              message="Loading profile data..."
              minHeight={400}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <ProfileUpdateForm />
              <PasswordUpdateForm />
            </Box>
          )}
        </DialogContent>
      </DialogWindow>
    </>
  );
};

export default ProfileDialog;
